import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";

import apiHelper from "../ApiHelpar/apiHelper";
import useDebounce from "../useDebounce/useDebounce";
import swal from "sweetalert";

import {
  faPen,
  faTrash,
  faSearch,
  faSortAmountDown,
  faFileExport,
  faExclamationTriangle,
  faSync,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import {
  ACTIVE_STATUSBYHOSPITAL_DOCOTOR_ID,
  SEARCH_DOCTOR_WITH_PAGINATIO,
} from "../visitConstant/ApiConstant";
import HospitalDataComponent from "./HospitalDataComponent";

const DoctorAllBranchesTrickList = ({
  expandedView,
  setDataEdit,
  RefreshApi,
  hospitalId,
  BaranchId,
  Role,
}) => {
  // State management
  const [doctorList, setDoctorList] = useState([]);
  const [doctor, setDoctor] = useState();
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [dataListActive, setDataListActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Fetch doctors
  const fetchDoctors = useCallback(
    async (page = currentPage) => {
      setLoading(true);
      setError(null);

      try {
        const offset = (page - 1) * itemsPerPage;

        const isNumericSearch = /^\d+$/.test(debouncedSearchTerm);

        const response = await apiHelper(SEARCH_DOCTOR_WITH_PAGINATIO, {
          //name: debouncedSearchTerm.trim() || "",
          // mobileNo: debouncedSearchTerm.trim() || "",

          [isNumericSearch ? "mobileNo" : "name"]: debouncedSearchTerm,

          hospitalId: hospitalId,
          branchId: BaranchId,
          offset,
          pageSize: itemsPerPage,
          active: dataListActive,
        });

        // console.log("response--------->>>>>>>>", response);
        if (response?.respData?.code === "0") {
          const doctors = response.respData?.respMsg?.content || [];
          setDoctorList(doctors);
          setTotalItems(response.respData?.respMsg?.totalElements || 0);
        } else {
          throw new Error(
            response?.respData?.message || "Failed to fetch doctors"
          );
        }
      } catch (error) {
        console.error("Error fetching doctors:", error);
        setError(error.message || "An unexpected error occurred");
        setDoctorList([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [
      currentPage,
      itemsPerPage,
      debouncedSearchTerm,
      dataListActive,
      sortField,
      sortDirection,
    ]
  );

  // Reset to first page when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearchTerm,
    dataListActive,
    sortField,
    sortDirection,
    itemsPerPage,
  ]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchDoctors(currentPage);
  }, [fetchDoctors, currentPage, RefreshApi]);

  // Handle doctor status change
  const handleStatusChange = async (doctor) => {
    const action = dataListActive ? "deactivate" : "activate";

    swal({
      title: "Are you sure?",
      text: `Do you want to ${action} this doctor?`,
      icon: "warning",
      buttons: ["Cancel", "Confirm"],
      dangerMode: dataListActive,
    }).then(async (confirmed) => {
      if (confirmed) {
        try {
          const response = await apiHelper(ACTIVE_STATUSBYHOSPITAL_DOCOTOR_ID, {
            customerId: doctor.customerId,
            active: !dataListActive,
          });

          if (response?.respData?.code === "0") {
            swal("Success!", `Doctor ${action}d successfully.`, "success");
            fetchDoctors(); // Refresh the list
          } else {
            throw new Error(
              response?.respData?.message || `Failed to ${action} doctor`
            );
          }
        } catch (error) {
          swal(
            "Error!",
            error.message || `Failed to ${action} doctor.`,
            "error"
          );
        }
      }
    });
  };

  // Export to CSV
  const exportToCSV = async () => {
    setExportLoading(true);

    try {
      const response = await apiHelper(SEARCH_DOCTOR_WITH_PAGINATIO, {
        name: debouncedSearchTerm,
        // hospitalId: 0,
        // branchId: 1,
        offset: 0,
        pageSize: totalItems,
        active: dataListActive,
      });

      if (response?.respData?.code === "0") {
        const dataToExport = response.respData?.respMsg?.content || [];

        if (dataToExport.length === 0) {
          swal("No Data", "There are no records to export.", "info");
          return;
        }

        // Prepare CSV content
        const csvHeader =
          "ID,Name,Mobile,Email,Specialization,Registration No,Status\n";
        const csvRows = dataToExport
          .map(
            (doctor) =>
              `${doctor.customerId},"${doctor.name.replace(/"/g, '""')}","${
                doctor.mobileNo
              }","${doctor.emailId}","${
                doctor.doctors[0]?.specialization || "N/A"
              }","${doctor.doctors[0]?.registrationNo || "N/A"}",${
                dataListActive ? "Active" : "Inactive"
              }`
          )
          .join("\n");

        const csvContent = csvHeader + csvRows;
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);

        // Create download link
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          `doctors_list_${moment().format("YYYYMMDD_HHmmss")}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        swal("Success!", "Export completed successfully.", "success");
      } else {
        throw new Error(response?.respData?.message || "Failed to export data");
      }
    } catch (error) {
      console.error("Export error:", error);
      swal(
        "Export Failed",
        error.message || "An error occurred during export.",
        "error"
      );
    } finally {
      setExportLoading(false);
    }
  };

  // View doctor details
  const handleViewDoctor = (doctor) => {
    swal({
      title: "Doctor Details",
      html: `
        <div class="text-start">
          <p><strong>Name:</strong> ${doctor.name}</p>
          <p><strong>Email:</strong> ${doctor.emailId}</p>
          <p><strong>Mobile:</strong> ${doctor.mobileNo}</p>
          <p><strong>Specialization:</strong> ${
            doctor.doctors[0]?.specialization || "N/A"
          }</p>
          <p><strong>Registration No:</strong> ${
            doctor.doctors[0]?.registrationNo || "N/A"
          }</p>
          <p><strong>Registration Date:</strong> ${
            doctor.doctors[0]?.registrationDate
              ? moment(doctor.doctors[0].registrationDate).format("DD-MMM-YYYY")
              : "N/A"
          }</p>
        </div>
      `,
      button: "Close",
    });
  };

  const ShowModal = (doctor) => {
    setDoctor(doctor);
    setIsModalOpen(true);
  };

  return (
    <div className="container-fluid p-0">
      {/* Search and Action Buttons */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-3 px-3 mt-3">
        <div className="d-flex flex-column flex-sm-row gap-2 w-100">
          <div className="input-group">
            <span
              className={`input-group-text ${
                dataListActive ? "bg-white" : "bg-primary text-white"
              }`}
              onClick={() => setDataListActive(!dataListActive)}
              style={{ cursor: "pointer" }}
              title={
                dataListActive
                  ? "Showing active doctors"
                  : "Showing inactive doctors"
              }
            >
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name or mobile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search doctors"
            />
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => setSearchTerm("")}
              disabled={!searchTerm}
            >
              Clear
            </button>
          </div>

          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={exportToCSV}
              disabled={loading || exportLoading || doctorList.length === 0}
            >
              {exportLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Exporting...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faFileExport} className="me-2" />
                  Export
                </>
              )}
            </button>

            <button
              className="btn btn-outline-primary"
              onClick={() => fetchDoctors(currentPage)}
              disabled={loading}
              title="Refresh data"
            >
              <FontAwesomeIcon icon={faSync} spin={loading} />
            </button>
          </div>
        </div>
      </div>

      {/* Error Handling */}
      {error && (
        <div className="alert alert-danger mx-3" role="alert">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {error}
          <button
            type="button"
            className="btn-close float-end"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {/* Doctor List Table */}
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Name</th>
              <th scope="col">Mobile</th>
              <th scope="col">Specialization</th>
              <th scope="col">Registration No</th>
              <th scope="col" className="text-center">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {doctorList.length > 0 ? (
              doctorList.map((doctor, index) => (
                <tr key={doctor.customerId} onClick={() => ShowModal(doctor)}>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>{doctor.name}</td>
                  <td>{doctor.mobileNo}</td>
                  <td>{doctor.doctors?.specialization || "Not Specified"}</td>
                  <td>{doctor.doctors?.registrationNo || "Not Available"}</td>
                  <td>
                    <div className="d-flex justify-content-center gap-2">
                      {/* <button
                        className="btn btn-sm btn-info"
                        onClick={() => handleViewDoctor(doctor)}
                        title="View Doctor Details"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button> */}
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => setDataEdit(doctor)}
                        title="Edit Doctor"
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button
                        className={`btn btn-sm ${
                          dataListActive ? "btn-danger" : "btn-success"
                        }`}
                        onClick={() => handleStatusChange(doctor)}
                        title={
                          dataListActive
                            ? "Deactivate Doctor"
                            : "Activate Doctor"
                        }
                      >
                        <FontAwesomeIcon
                          icon={dataListActive ? faTrash : faSync}
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center py-5">
                  {loading ? (
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    <div className="text-muted">
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="me-2"
                        size="lg"
                      />
                      No doctors found matching your criteria
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Section (keep existing implementation) */}

      {doctorList.length > 0 && (
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center px-3 mt-3 mb-3">
          <div className="text-muted mb-3 mb-md-0">
            Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
            entries
          </div>
          <nav aria-label="Doctor list pagination">
            <ul className="pagination pagination-sm mb-0 align-items-center">
              {/* Previous Button */}
              <li
                className={`page-item me-2 ${
                  currentPage === 1 ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  Previous
                </button>
              </li>

              {/* Page Number Buttons */}
              {(() => {
                const pageButtons = [];
                const maxVisiblePages = 5;
                const halfVisiblePages = Math.floor(maxVisiblePages / 2);

                // Calculate start and end pages
                let startPage = Math.max(1, currentPage - halfVisiblePages);
                let endPage = Math.min(
                  Math.ceil(totalItems / itemsPerPage),
                  startPage + maxVisiblePages - 1
                );

                // Adjust start and end pages to maintain consistent button count
                if (endPage - startPage + 1 < maxVisiblePages) {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1);
                }

                // Add first page button if not already visible
                if (startPage > 1) {
                  pageButtons.push(
                    <li key="first" className="page-item">
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(1)}
                      >
                        1
                      </button>
                    </li>
                  );
                  if (startPage > 2) {
                    pageButtons.push(
                      <li key="first-ellipsis" className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    );
                  }
                }

                // Generate page buttons
                for (let page = startPage; page <= endPage; page++) {
                  pageButtons.push(
                    <li
                      key={page}
                      className={`page-item ${
                        currentPage === page ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    </li>
                  );
                }

                // Add last page button if not already visible
                const totalPageCount = Math.ceil(totalItems / itemsPerPage);
                if (endPage < totalPageCount) {
                  if (endPage < totalPageCount - 1) {
                    pageButtons.push(
                      <li key="last-ellipsis" className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    );
                  }
                  pageButtons.push(
                    <li key="last" className="page-item">
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(totalPageCount)}
                      >
                        {totalPageCount}
                      </button>
                    </li>
                  );
                }

                return pageButtons;
              })()}

              {/* Next Button */}
              <li
                className={`page-item ms-2 ${
                  currentPage === Math.ceil(totalItems / itemsPerPage)
                    ? "disabled"
                    : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={
                    currentPage === Math.ceil(totalItems / itemsPerPage)
                  }
                  aria-label="Next page"
                >
                  Next
                </button>
              </li>

              {/* Items Per Page Dropdown */}
              <li className="page-item ms-3">
                <select
                  className="form-select form-select-sm"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing items per page
                  }}
                  style={{
                    width: "auto",
                    height: "calc(1.5em + 0.5rem + 2px)",
                  }}
                >
                  {[5, 10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size} per page
                    </option>
                  ))}
                </select>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {isModalOpen && (
        <HospitalDataComponent
          data={doctor}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
};

export default DoctorAllBranchesTrickList;
